import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePreferences } from "../../preferences/preferencesContext";
import styles from "./PartialState.module.css";

/** How long the notice stays before it leaves on its own, unless the pointer or keyboard focus is on it. */
const ShownForMs = 6000;

/**
 * The shared "one vendor's data is missing" notice (`QR-07`): the rest of a page's data is still
 * shown, and this names the specific vendor rather than degrading the whole screen to a generic
 * error. Dismissing it is the only thing a person can do with it, so it also leaves on its own after
 * a few seconds; a thin bar counts down, and pointing at it or tabbing into it pauses the count so it
 * can be read. People who never want it can switch provider warnings off in their preferences.
 *
 * @param props.vendorName - The vendor (or vendors) the gap is in, named explicitly: the person needs
 *   to know which of their tools this affects, whatever the description says.
 * @param props.description - Overrides the default explanatory text.
 */
export function PartialState({ vendorName, description }: { vendorName: string; description?: string }): React.JSX.Element | null {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"shown" | "leaving" | "gone">("shown");
  const { preferences } = usePreferences();

  // With reduced motion there is no transition to wait for, so the notice goes at once.
  const leave = (): void => setPhase(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "gone" : "leaving");

  // Hidden only when the person has switched provider warnings off, or once it has gone.
  if (phase === "gone" || preferences.showProviderWarnings === false) {
    return null;
  }

  return (
    <div
      className={phase === "leaving" ? `${styles.wrap} ${styles.leaving}` : styles.wrap}
      onTransitionEnd={(event) => {
        if (phase === "leaving" && event.target === event.currentTarget) {
          setPhase("gone");
        }
      }}
    >
      <div className={styles.notice} role="status">
        <span className={styles.icon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
        </span>
        <div className={styles.text}>
          <p className={styles.title}>{t("states.partial.title", { vendorName })}</p>
          <p className={styles.description}>{description ?? t("states.partial.defaultDescription", { vendorName })}</p>
        </div>
        <button type="button" className={styles.dismiss} aria-label={t("states.partial.dismiss")} onClick={leave}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        {/* The countdown: when its animation ends, the notice leaves. Pausing the animation pauses the countdown. */}
        <span
          className={styles.countdown}
          style={{ animationDuration: `${String(ShownForMs)}ms` }}
          onAnimationEnd={leave}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
